// src/components/login/admin/admin-brand-keys-page.tsx
//
// Admin-only: configure a brand's own LLM API keys. Each provider is set
// independently to either the platform's shared global key or the brand's own
// custom key. The brand-level mode ('custom' if any provider is custom, else
// 'global') is derived from those per-provider choices and sent on save.
//
// Security: keys are write-only from the UI's perspective — the backend never
// returns them, only {configured, last4}. A newly-entered key is validated with
// its provider before it can be saved; a wrong key is never persisted.
//
// -- Migrated onto the kit, Phase 6 ------------------------------------------
//
// `Button`, `SearchInput` (17), `TextInput` (13), `Segmented` (09),
// `Banner` (62), `Validation` (27), `EmptyState` (76), `SkeletonCard` (69).
// `data-kit` on the root. `ui/button` is gone.
//
// What did NOT move: `brandApiKeysApi`, `brandsApi`, the per-provider choice
// model, `friendlyKeyError`, the test-before-save rule and every string.
//
// Two things the swap fixed:
//
//   * The per-provider Global / Brand key control was two buttons with no
//     group role, no `aria-pressed` and no keyboard relationship. It is item
//     09's segmented control: one tab stop, arrows between options.
//   * `var(--signal-green-5, var(--card))` — a fallback for a token that does
//     not exist, so the success box was painted `--card` and its own border
//     was the only thing saying it was a success.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  brandApiKeysApi,
  LLM_PROVIDERS,
  PROVIDER_LABELS,
  type BrandLlmKeys,
  type LlmProvider,
} from "@/api/brandApiKeys";
import { brandsApi, type AssignableBrand } from "@/api/brands";
import { KeyRound, RefreshCcw, Save } from "lucide-react";
import { ModuleHeader } from "../../shell/module-header";
import { BrandAimodeBackupSection } from "./brand-aimode-backup-section";
import {
  Banner, Button, EmptyState, SearchInput, Segmented, SkeletonCard,
  TextInput, Validation, cx,
} from "../../kit";

type ProviderChoice = "global" | "custom";

function isAdminFromStorage() {
  return (
    localStorage.getItem("is_admin") === "true" ||
    sessionStorage.getItem("is_admin") === "true"
  );
}

/** Turn a raw provider/SDK error (often a 401 JSON blob) into one friendly line. */
function friendlyKeyError(detail: string): string {
  const d = (detail || "").toLowerCase();
  if (
    d.includes("401") ||
    d.includes("incorrect api key") ||
    d.includes("invalid_request_error") ||
    d.includes("invalid x-api-key") ||
    d.includes("api key not valid") ||
    d.includes("api_key_invalid") ||
    d.includes("authentication") ||
    d.includes("unauthor")
  ) {
    return "This API key isn't valid. Double-check it and paste it again.";
  }
  if (
    d.includes("429") ||
    d.includes("quota") ||
    d.includes("rate limit") ||
    d.includes("insufficient_quota")
  ) {
    return "Key recognised, but it's rate-limited or out of quota.";
  }
  if (d.includes("403") || d.includes("permission") || d.includes("forbidden")) {
    return "This key doesn't have access to this provider.";
  }
  if (
    d.includes("timeout") ||
    d.includes("network") ||
    d.includes("connection") ||
    d.includes("could not") ||
    d.includes("failed to fetch")
  ) {
    return "Couldn't reach the provider to validate this key. Try again.";
  }
  return "Couldn't validate this key. Please re-check it and try again.";
}

const emptyChoices = (): Record<LlmProvider, ProviderChoice> =>
  LLM_PROVIDERS.reduce(
    (acc, p) => ({ ...acc, [p]: "global" as ProviderChoice }),
    {} as Record<LlmProvider, ProviderChoice>,
  );

export function AdminBrandKeysPage() {
  const [brands, setBrands] = useState<AssignableBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);

  const [config, setConfig] = useState<BrandLlmKeys | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Per-provider: global vs custom, plus any newly-typed key.
  const [choices, setChoices] = useState<Record<LlmProvider, ProviderChoice>>(emptyChoices);
  const [drafts, setDrafts] = useState<Partial<Record<LlmProvider, string>>>({});

  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<LlmProvider | null>(null);
  // Which endpoint to test the Perplexity key against (#90): Sonar chat
  // completions (retiring 2026-09-27) or the Agent API. Every other provider
  // has only one call surface, so this only ever applies to "perplexity".
  const [perplexityTestEndpoint, setPerplexityTestEndpoint] = useState<"sonar" | "agent">("sonar");
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  // Per-provider inline validation message, shown under that provider's input.
  const [fieldMsg, setFieldMsg] = useState<
    Partial<Record<LlmProvider, { ok: boolean; text: string }>>
  >({});

  const clearFieldMsg = (provider: LlmProvider) =>
    setFieldMsg((m) => {
      if (!m[provider]) return m;
      const next = { ...m };
      delete next[provider];
      return next;
    });

  // ── Load brands ────────────────────────────────────────────────────
  const loadBrands = useCallback(async () => {
    setErr("");
    setLoadingBrands(true);
    try {
      if (!isAdminFromStorage()) {
        setErr("Admin access required.");
        setBrands([]);
        return;
      }
      setBrands(await brandsApi.assignable());
    } catch (e: any) {
      setErr(e?.message || "Failed to load brands");
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // ── Load a brand's key config ──────────────────────────────────────
  const applyConfig = (cfg: BrandLlmKeys) => {
    setConfig(cfg);
    setFieldMsg({});
    // A provider is "custom" when (and only when) it has a stored key.
    setChoices(
      LLM_PROVIDERS.reduce(
        (acc, p) => ({
          ...acc,
          [p]: cfg.providers[p]?.configured ? "custom" : "global",
        }),
        {} as Record<LlmProvider, ProviderChoice>,
      ),
    );
  };

  const loadConfig = useCallback(async (brand: string) => {
    setErr("");
    setSuccess("");
    setLoadingConfig(true);
    setDrafts({});
    try {
      applyConfig(await brandApiKeysApi.get(brand));
    } catch (e: any) {
      setErr(e?.message || "Failed to load brand keys");
      setConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  const selectBrand = (brand: string) => {
    setSelectedBrand(brand);
    loadConfig(brand);
  };

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.brand_name.toLowerCase().includes(q));
  }, [brands, search]);

  // ── Save ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedBrand) return;
    setErr("");
    setSuccess("");
    setSaving(true);
    try {
      const keys: Partial<Record<LlmProvider, string>> = {}; // new keys to store
      const toDelete: LlmProvider[] = []; // custom→global on a configured provider
      const incomplete: LlmProvider[] = []; // custom chosen but no key available

      for (const p of LLM_PROVIDERS) {
        const draft = (drafts[p] || "").trim();
        const configured = !!config?.providers[p]?.configured;
        if (choices[p] === "custom") {
          if (draft) keys[p] = draft;
          else if (!configured) incomplete.push(p);
        } else if (configured) {
          // Switched a configured provider back to global → remove its key.
          toDelete.push(p);
        }
      }

      if (incomplete.length) {
        setFieldMsg((m) => {
          const next = { ...m };
          incomplete.forEach((p) => {
            next[p] = { ok: false, text: "Enter a key, or switch back to Global." };
          });
          return next;
        });
        setErr("Some providers need attention — see the messages below.");
        return;
      }

      // Validate every newly-entered key with its provider BEFORE persisting.
      // A wrong key must never be saved — block the whole save if any fail.
      const entered = Object.entries(keys) as [LlmProvider, string][];
      if (entered.length) {
        const checks = await Promise.all(
          entered.map(async ([provider, key]) => {
            try {
              const res = await brandApiKeysApi.test(selectedBrand, provider, key);
              return { provider, ok: res.ok, detail: res.detail };
            } catch (e: any) {
              return { provider, ok: false, detail: e?.message || "Validation failed" };
            }
          }),
        );
        const failures = checks.filter((c) => !c.ok);
        if (failures.length) {
          setFieldMsg((m) => {
            const next = { ...m };
            failures.forEach((f) => {
              next[f.provider] = { ok: false, text: friendlyKeyError(f.detail) };
            });
            return next;
          });
          setErr("Not saved — fix the highlighted keys below.");
          return;
        }
      }

      // Apply removals first, then upsert new keys + the derived brand mode.
      for (const p of toDelete) {
        await brandApiKeysApi.remove(selectedBrand, p);
      }

      const anyCustom = LLM_PROVIDERS.some(
        (p) =>
          choices[p] === "custom" &&
          (!!config?.providers[p]?.configured || !!(drafts[p] || "").trim()),
      );

      const updated = await brandApiKeysApi.update(selectedBrand, {
        mode: anyCustom ? "custom" : "global",
        keys: Object.keys(keys).length ? keys : undefined,
      });
      applyConfig(updated);
      setDrafts({});
      setSuccess("Changes saved.");
    } catch (e: any) {
      setErr(e?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider: LlmProvider) => {
    if (!selectedBrand) return;
    setErr("");
    setSuccess("");
    clearFieldMsg(provider);
    setTesting(provider);
    try {
      const draft = (drafts[provider] || "").trim();
      const endpoint = provider === "perplexity" ? perplexityTestEndpoint : undefined;
      const res = await brandApiKeysApi.test(selectedBrand, provider, draft || undefined, endpoint);
      const endpointLabel = res.endpoint === "agent" ? "Agent API" : res.endpoint === "sonar" ? "Sonar" : null;
      setFieldMsg((m) => ({
        ...m,
        [provider]: res.ok
          ? { ok: true, text: endpointLabel ? `Key is valid (${endpointLabel}).` : "Key is valid." }
          : { ok: false, text: friendlyKeyError(res.detail) },
      }));
    } catch (e: any) {
      setFieldMsg((m) => ({
        ...m,
        [provider]: { ok: false, text: friendlyKeyError(e?.message || "") },
      }));
    } finally {
      setTesting(null);
    }
  };

  // BrandAimodeBackupSection (from main, #89) still takes hand-rolled styles
  // rather than kit components; these map its two props onto V2 tokens.
  const backupInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 9,
    border: "var(--bd-w) solid var(--bd-default)",
    backgroundColor: "var(--s-raised)",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    color: "var(--tx-default)",
  };
  const backupSegBtn = (active: boolean): React.CSSProperties => ({
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
    border: "none",
    borderRadius: 7,
    backgroundColor: active ? "var(--a-brand-sub)" : "transparent",
    color: active ? "var(--a-brand-t)" : "var(--tx-muted)",
    fontWeight: active ? 600 : 400,
  });

  return (
    <div className="upm-page" data-kit>
      <ModuleHeader
        page="Brand API Keys"
        icon={KeyRound}
        compact
        title="Brand API keys"
        description="Admin-only. Give a paying brand its own LLM provider keys so its usage is billed to the brand's own accounts instead of the platform's shared keys."
        actionsSlot={
          <Button
            variant="secondary"
            icon={RefreshCcw}
            onClick={loadBrands}
            disabled={loadingBrands}
            loading={loadingBrands}
            loadingLabel="Refreshing"
          >
            Refresh
          </Button>
        }
      />

      <div className="shell-page upm-page__body">
        <section className="upm-admin__note">
          <span className="k-meta">How it works</span>
          <ol className="upm-admin__steps">
            <li>Pick a brand from the list on the left.</li>
            <li>
              For each provider, choose <strong>Global</strong> to use the
              platform&apos;s shared key, or <strong>Brand key</strong> to enter a key
              for this brand only.
            </li>
            <li>
              Use <strong>Test</strong> to verify a key with the provider, then{" "}
              <strong>Save</strong>. A wrong key is rejected and won&apos;t be saved.
            </li>
            <li>
              Saved keys are encrypted and never shown again. Only the last 4
              characters are displayed.
            </li>
          </ol>
        </section>

        {err ? <Banner tone="error">{err}</Banner> : null}
        {success ? <Banner tone="ok">{success}</Banner> : null}

        <div className="upm-admin__split">
          {/* Brand picker */}
          <div className="upm-admin__panel">
            <SearchInput
              type="search"
              name="brand-key-search"
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch("")}
              placeholder="Search brands"
              aria-label="Search brands"
            />

            {loadingBrands ? (
              <SkeletonCard />
            ) : (
              <div className="upm-admin__list" role="listbox" aria-label="Brands">
                {filteredBrands.map((b) => (
                  <button
                    key={b.brand_name}
                    type="button"
                    role="option"
                    aria-selected={selectedBrand === b.brand_name}
                    className={cx("upm-admin__pick")}
                    onClick={() => selectBrand(b.brand_name)}
                  >
                    {b.brand_name}
                  </button>
                ))}
                {filteredBrands.length === 0 && (
                  <p className="upm-admin__quiet">No brands found.</p>
                )}
              </div>
            )}
          </div>

          {/* Config panel */}
          <div className="upm-admin__panel">
            {!selectedBrand ? (
              <EmptyState
                icon={KeyRound}
                title="No brand selected"
                description="Select a brand to configure its API keys."
              />
            ) : loadingConfig ? (
              <SkeletonCard />
            ) : (
              <>
                <header className="upm-admin__panel-head">
                  <h2 className="upm-admin__panel-title">{selectedBrand}</h2>
                  <span className="k-meta">
                    Set each provider to the shared global key or this brand&apos;s own key
                  </span>
                </header>

                <div className="upm-admin__providers">
                  {LLM_PROVIDERS.map((provider) => {
                    const status = config?.providers[provider];
                    const configured = !!status?.configured;
                    const isCustom = choices[provider] === "custom";
                    return (
                      <div className="upm-admin__provider" key={provider}>
                        <div className="upm-admin__provider-head">
                          <span className="upm-admin__provider-name">
                            {PROVIDER_LABELS[provider]}
                          </span>
                          <Segmented
                            label={`${PROVIDER_LABELS[provider]} key source`}
                            value={isCustom ? "custom" : "global"}
                            onChange={(v) => {
                              setChoices((c) => ({ ...c, [provider]: v as ProviderChoice }));
                              clearFieldMsg(provider);
                            }}
                            options={[
                              { value: "global", label: "Global" },
                              { value: "custom", label: "Brand key" },
                            ]}
                          />
                        </div>

                        {isCustom ? (
                          <>
                            <div className="upm-admin__provider-hint">
                              <span>
                                {configured
                                  ? "Leave blank to keep the current key."
                                  : "Enter this brand's key for this provider."}
                              </span>
                              {configured && (
                                <Validation tone="ok">
                                  Configured, ending {status?.last4}
                                </Validation>
                              )}
                            </div>
                            {provider === "perplexity" && (
                              // Which endpoint to test the key against (#90):
                              // Sonar chat completions or the Agent API.
                              <div className="upm-admin__provider-hint">
                                <span>Test against</span>
                                <Segmented
                                  label="Perplexity test endpoint"
                                  value={perplexityTestEndpoint}
                                  onChange={(v) => {
                                    setPerplexityTestEndpoint(v as "sonar" | "agent");
                                    clearFieldMsg(provider);
                                  }}
                                  options={[
                                    { value: "sonar", label: "Sonar" },
                                    { value: "agent", label: "Agent" },
                                  ]}
                                />
                              </div>
                            )}
                            <div className="upm-admin__provider-row">
                              <TextInput
                                className="upm-admin__provider-field"
                                type="password"
                                name={`brand-key-${provider}`}
                                autoComplete="new-password"
                                data-lpignore="true"
                                data-1p-ignore
                                aria-label={`${PROVIDER_LABELS[provider]} API key`}
                                placeholder={configured ? "Enter a new key to replace" : "Paste API key"}
                                value={drafts[provider] ?? ""}
                                onChange={(e) => {
                                  setDrafts((d) => ({ ...d, [provider]: e.target.value }));
                                  clearFieldMsg(provider);
                                }}
                              />
                              <Button
                                type="button"
                                variant="secondary"
                                disabled={testing === provider}
                                loading={testing === provider}
                                loadingLabel="Testing"
                                onClick={() => handleTest(provider)}
                              >
                                Test
                              </Button>
                            </div>
                            {fieldMsg[provider] && (
                              <Validation tone={fieldMsg[provider]!.ok ? "ok" : "error"}>
                                {fieldMsg[provider]!.text}
                              </Validation>
                            )}
                          </>
                        ) : (
                          <p className="upm-admin__quiet">
                            Using the platform&apos;s shared global key.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="upm-admin__actions">
                  <Button
                    icon={Save}
                    onClick={handleSave}
                    disabled={saving}
                    loading={saving}
                    loadingLabel="Saving"
                  >
                    Save
                  </Button>
                </div>

                {/* Google AI Mode backup (SerpApi), per brand (#89). Saves on
                    its own, separately from the LLM keys above. */}
                <BrandAimodeBackupSection
                  brand={selectedBrand}
                  inputStyle={backupInputStyle}
                  segBtn={backupSegBtn}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminBrandKeysPage;
